/**
 * Implements a list of pairs of this wallets in this period
 */
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AutoSizer, Column, Table } from 'react-virtualized';
import { actionShowModal } from '../../../actions/modal';
import { actionCreateNotification } from '../../../actions/notifications';
import PairIcons from '../../../molecules/PairIcons';
import { IStore } from '../../../reducers';
import { ModalTypes } from '../../../reducers/modal';
import { KattanaAPI } from '../../../store/createStore';
import { Loader } from '../../DexTrades';
import { formatTimestamp } from '../../DexTrades/formatters';
import ErrorPlaceholder from '../ErrorPlaceholder';
import { getChainsArrayFromTradesArray } from '../WalletModal';
import ScanButton from '../WalletModal/ScanButton';
import { protocolToExchange } from './helpers';
import './styles.scss';


interface IPairsTableProps {
  data: any
  actionShowModal: (modalName: ModalTypes, props?: any) => void;
  modalProps: any
}

const PairsTable = ({ data, actionShowModal, modalProps }: IPairsTableProps) => {
  const onRowClicked = (rowData: any) => {
    actionShowModal(ModalTypes.wallet, { ...modalProps, address: rowData.pair, exchange: protocolToExchange(rowData.protocol), history: modalProps.history ? [modalProps, ...modalProps.history] : [modalProps]});
  };


  return ( <AutoSizer>
    {({ width, height }) => (
      <Table
        width={width}
        height={height - 4}
        rowCount={data?.length || 0}
        rowHeight={35}
        headerHeight={20}
        rowGetter={({ index }) => data[index]}
        rowClassName={({index}) => index === -1 ? '' : 'wallet_pairs__row'} // prevent header hover
        rowStyle={{ width }}
        headerClassName='trades__table__header'
        onRowClick={({rowData}) => onRowClicked(rowData)}
      >
        <Column
          width={width}
          className="trades__table__cell trades__table--first"
          dataKey='ticker'
          label='Pair'
          headerStyle={{ textTransform: 'none', paddingLeft: '14px' }} // first element has this padding
          cellRenderer={({ rowData, cellData }) => {
            if (!rowData.ticker) {
              return (<div className="wallet_modal__pair">
                <span className="wallet_modal__pair__pairTitle">{rowData.pair}</span>
              </div>);
            }
            return (<div className="wallet_modal__pair">
              <PairIcons
                chain={cellData.chain}
                pair={cellData.name}
                base_address={cellData.base_address}
                quote_address={cellData.quote_address}
                base_slug={cellData.base_slug}
                quote_slug={cellData.quote_slug}
              /><span className="wallet_modal__pair__pairTitle">{cellData.name}</span>
            </div>);
          }}
        />
        <Column
          width={width}
          className="trades__table__cell wallet_modal__tradesCount"
          dataKey='tradesCount'
          label='Trades'
          headerStyle={{ textTransform: 'none' }}
          cellRenderer={ ({ rowData }) => (<span>
            <span className="wallet_modal__pair__buyCount">{rowData.buyCount}</span> / <span className="wallet_modal__pair__sellCount">{rowData.sellCount}</span>
          </span>)}
        />
        <Column
          width={width}
          className="trades__table__cell wallet_modal__date"
          dataKey='first_trade'
          label='First Trade'
          headerStyle={{ textTransform: 'none' }}
          cellRenderer={({cellData}) => <span>{formatTimestamp(cellData)}</span>}
        />
        <Column
          width={width}
          className="trades__table__cell wallet_modal__date"
          dataKey='lastTrade'
          label='Last Trade'
          headerStyle={{ textTransform: 'none' }}
          cellRenderer={({cellData}) => <span>{formatTimestamp(cellData)}</span>}
        />
      </Table>
    )}
  </AutoSizer>);
};

/**
 * Decorates timeframe
 * @param input
 */
const decorateTimeframe = (input: string): string => {
  switch (input) {
  case 'day':
    return 'daily';
  case 'week':
    return 'weekly';
  case 'month':
    return 'monthly';
  case 'all':
    return 'since 01.01.2021';
  default:
    return input;
  }
};

const WalletPairsModal = ({ actionCloseModal, modalProps, actionShowModal, actionCreateNotification }: any) => {
  const { wallet, timeframe } = modalProps;
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(false);
  const [data, setData] = useState([]);
  const [chains, setChains] = useState<string[]>([]);

  const onShare = () => {
    navigator.clipboard.writeText(`${window.location.origin}/wallet/${wallet}/${timeframe}`);
    actionCreateNotification({ message: 'Link copied to clipboard' });
  };

  useEffect(() => {
    KattanaAPI.walletPairs(wallet + '21', timeframe).then((data: any) => {
      setChains(getChainsArrayFromTradesArray(data));
      setData(data);
      setError(false);
    }).catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const onBackClick = () => {
    const goBack = modalProps.history[0];
    actionShowModal(ModalTypes.wallet, goBack);
  };

  return (<>
    <div className='wallet_modal walletPairModal'>
      {!isLoading && isError && <ErrorPlaceholder />}
      <div className='wallet_modal__close' onClick={actionCloseModal} />
      <div className='wallet_modal__header'>
        {modalProps.history && modalProps.history[0] && <div className='wallet_modal__back' onClick={onBackClick} />}
        <span>{wallet} {decorateTimeframe(timeframe)}</span>
        <span className='wallet_modal__pill wallet_modal__share' onClick={onShare}>Share</span>
        {chains.map((chain: string) => <ScanButton key={chain} chain={chain} wallet={wallet}/>)}
      </div>
      <div className='wallet_modal__container'>

        <div className="wallet_modal__trades">
          <div className="trades__table">
            {isLoading && !isError && <Loader />}
            {!isLoading && !isError && <PairsTable modalProps={modalProps} actionShowModal={actionShowModal} data={data} />}
          </div>
        </div>
      </div>
    </div>

    <div className='wallet_modal__backdrop' onClick={actionCloseModal} />
  </>);
};


const mapStateToProps = (state: IStore) => ({
  modalProps: state.modal.props
});

export default connect(mapStateToProps, { actionShowModal, actionCreateNotification })(WalletPairsModal);